/**
 * Diagnóstico de Pagamentos — porque falham.
 *
 * Lê erro_codigo/erro_mensagem de pg_pagamentos_sisp. Esses campos existiam
 * mas ficavam vazios: o motivo era guardado apenas dentro do JSON
 * dados_pagamento, onde não é possível filtrar nem agrupar. Desde 2026-07 o
 * callback preenche as colunas, e os registos anteriores foram recuperados.
 *
 * O objectivo desta página é que "porque falham os pagamentos?" deixe de ser
 * uma investigação e passe a ser um ecrã.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ReportProblemOutlined as ReportProblemIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Button from '@mui/material/Button';
import reportsService, { DiagnosticoFalhas } from '../../services/reports.service';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';

const cve = (v: number) => `${Math.round(v).toLocaleString('pt-PT')} CVE`;

/** Verde acima de 60%, âmbar entre 30 e 60, vermelho abaixo. */
const corTaxa = (t: number): 'success' | 'warning' | 'error' =>
  t >= 60 ? 'success' : t >= 30 ? 'warning' : 'error';

/** Rótulos legíveis para os códigos de serviço. */
const LABEL: Record<string, string> = {
  RCG: 'Recargas', RCP: 'Carregamento carteira', WBS: 'Facturas de serviços',
  WEBS: 'Facturas de serviços', WEBSR: 'Pagamento por referência',
  ORDER: 'Pedidos da loja', WALLET: 'Carteira', ECM: 'E-commerce',
  PSHOP: 'Pagali Shop', DNT: 'Donativos', EVT: 'Eventos', QRC: 'QR Code',
  EST: 'Balcão virtual', FAT: 'Faturali', TOK: 'Tokenização',
};
const nomeServico = (s: string) => LABEL[s] || s;

export default function Falhas() {
  const [dados, setDados] = useState<DiagnosticoFalhas | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dias, setDias] = useState(180);
  const [servico, setServico] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setDados(await reportsService.getDiagnosticoFalhas(dias, servico || undefined));
      setErro(null);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Erro ao carregar diagnóstico');
    } finally {
      setLoading(false);
    }
  }, [dias, servico]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Diagnóstico de Pagamentos"
        subtitle="Porque falham, por serviço e por entidade"
        icon={<ReportProblemIcon />}
        actions={
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Período</InputLabel>
              <Select label="Período" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
                <MenuItem value={30}>30 dias</MenuItem>
                <MenuItem value={90}>90 dias</MenuItem>
                <MenuItem value={180}>180 dias</MenuItem>
                <MenuItem value={365}>1 ano</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Serviço</InputLabel>
              <Select label="Serviço" value={servico} onChange={(e) => setServico(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {(dados?.por_servico ?? []).map((s) => (
                  <MenuItem key={s.servico} value={s.servico}>{nomeServico(s.servico)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={carregar}>Actualizar</Button>
          </Stack>
        }
      />

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !dados ? (
        <EmptyState title="Sem dados" />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label="Tentativas" value={dados.totais.tentativas.toLocaleString('pt-PT')} color="primary" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Taxa de sucesso"
                value={`${dados.totais.taxa_sucesso}%`}
                color={corTaxa(dados.totais.taxa_sucesso)}
                helper={`${dados.totais.pagos.toLocaleString('pt-PT')} concluídos`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Valor não cobrado"
                value={cve(dados.totais.valor_perdido)}
                color="error"
                helper={`${dados.totais.falhados} falhados · ${dados.totais.expirados} expirados`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Falhas com motivo"
                value={`${dados.cobertura.pct}%`}
                color={dados.cobertura.pct >= 80 ? 'success' : 'warning'}
                helper={`${dados.cobertura.com_motivo} de ${dados.cobertura.total}`}
              />
            </Grid>
          </Grid>

          {dados.cobertura.pct < 100 && dados.cobertura.total > 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {dados.cobertura.total - dados.cobertura.com_motivo} falha(s) sem motivo registado —
              tipicamente sessões em que o callback do SISP nunca chegou.
            </Alert>
          )}

          {/* Motivos — a razão de ser desta página */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Motivos de falha</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                O que o SISP respondeu, agrupado. Ordenado por número de ocorrências.
              </Typography>
              {dados.motivos.length === 0 ? (
                <EmptyState compact title="Sem falhas no período" />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="right">Nº</TableCell>
                        <TableCell>Serviço</TableCell>
                        <TableCell align="center">Código</TableCell>
                        <TableCell>Motivo devolvido pelo SISP</TableCell>
                        <TableCell align="right">Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dados.motivos.map((m, i) => (
                        <TableRow key={`${m.servico}-${m.codigo}-${i}`} hover>
                          <TableCell align="right"><strong>{m.n}</strong></TableCell>
                          <TableCell>{nomeServico(m.servico)}</TableCell>
                          <TableCell align="center">
                            <Chip label={m.codigo} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 420 }}>
                            <Tooltip title={m.motivo}>
                              <Typography variant="body2" noWrap>{m.motivo}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{cve(m.valor)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Funil por serviço</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Serviço</TableCell>
                          <TableCell align="right">Tentativas</TableCell>
                          <TableCell sx={{ minWidth: 110 }}>Sucesso</TableCell>
                          <TableCell align="right">Não cobrado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dados.por_servico.map((s) => (
                          <TableRow key={s.servico} hover>
                            <TableCell>
                              <Typography variant="body2">{nomeServico(s.servico)}</Typography>
                              <Typography variant="caption" color="text.secondary">{s.servico}</Typography>
                            </TableCell>
                            <TableCell align="right">{s.tentativas}</TableCell>
                            <TableCell>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, s.taxa_sucesso)}
                                color={corTaxa(s.taxa_sucesso)}
                                sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {s.taxa_sucesso}% · {s.pagos}/{s.tentativas}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{cve(s.valor_perdido)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>Por entidade de destino</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Recargas e facturas, com 3 ou mais tentativas.
                  </Typography>
                  {dados.por_entidade.length === 0 ? (
                    <EmptyState compact title="Sem dados" />
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">Cód.</TableCell>
                            <TableCell>Entidade</TableCell>
                            <TableCell align="right">Tent.</TableCell>
                            <TableCell sx={{ minWidth: 90 }}>Sucesso</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dados.por_entidade.map((e, i) => (
                            <TableRow key={`${e.entidade}-${i}`} hover>
                              <TableCell align="center">
                                <Chip label={e.entidade} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>
                                <Typography variant="body2" noWrap>{e.nome}</Typography>
                              </TableCell>
                              <TableCell align="right">{e.tentativas}</TableCell>
                              <TableCell>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, e.taxa_sucesso)}
                                  color={corTaxa(e.taxa_sucesso)}
                                  sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                                />
                                <Typography variant="caption" color="text.secondary">{e.taxa_sucesso}%</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Evolução mensal</Typography>
                  {dados.evolucao.length === 0 ? (
                    <EmptyState compact title="Sem dados" />
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Mês</TableCell>
                            <TableCell align="right">Tentativas</TableCell>
                            <TableCell align="right">Concluídos</TableCell>
                            <TableCell sx={{ minWidth: 180 }}>Taxa de sucesso</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dados.evolucao.map((m) => (
                            <TableRow key={m.mes} hover>
                              <TableCell>{m.mes}</TableCell>
                              <TableCell align="right">{m.tentativas}</TableCell>
                              <TableCell align="right">{m.pagos}</TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, m.taxa_sucesso)}
                                    color={corTaxa(m.taxa_sucesso)}
                                    sx={{ height: 8, borderRadius: 4, flexGrow: 1 }}
                                  />
                                  <Typography variant="caption" sx={{ minWidth: 34 }}>{m.taxa_sucesso}%</Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
